import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import { Badge, BadgeAbsoluteContainer, Banner, TextShell } from '@qover/atoms';
import { LinkMixin, Heading3 } from '@qover/typography';
import { PrimaryButton } from '@qover/molecules';

const EmailIcon = () => (
  <svg width="17" height="12">
    <path d="M1.821 0C.823 0 0 .841 0 1.862v8.276C0 11.158.823 12 1.821 12H15.18c.998 0 1.821-.841 1.821-1.862V1.862C17 .842 16.177 0 15.179 0H1.82zm.133 1.241h13.098L8.873 6.88c-.235.215-.497.216-.733 0L1.954 1.241zm-.74.99l4.162 3.795-4.162 4.086V2.231zm14.572 0v7.875l-4.15-4.087 4.15-3.788zm-5.06 4.622l3.966 3.906H2.308l3.978-3.9 1.044.951c.663.605 1.69.606 2.353 0l1.043-.957z" />
  </svg>
);

const Container = styled.div`
  align-items: center;
  background-color: #fff;
  border-radius: ${({ theme }) => theme.borders.cardRadius};
  box-shadow: 0 1px 3px 0 ${({ theme }) => theme.colors.textQuote(0.15)};
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  position: relative;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.minWidth.S}) {
    max-width: 400px;

    &.grow {
      max-width: 100%;
    }
  }
`;

const InnerContainer = styled.div<{ small?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px;
  width: 100%;

  p {
    margin: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.minWidth.S}) {
    padding: ${props => (props.small ? '0 20px 20px' : '0 40px 20px')};
  }
`;

const FooterContainer = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.heroBackground()};
  display: flex;
  justify-content: center;
  padding: 11px 16px;
  width: 100%;

  a {
    color: ${({ theme }) => theme.colors.textQuote()};
    margin-left: 10px;
  }

  path {
    fill: ${({ theme }) => theme.colors.textQuote()};
  }
`;

const ActionButton = styled(PrimaryButton)<{ justify?: boolean }>`
  margin-top: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.minWidth.S}) {
    margin-top: 30px;
  }

  width: ${props => (props.justify ? '100%' : 'auto')};
`;

const DetailsLink = styled.a`
  ${LinkMixin};
  color: ${({ theme }) => theme.colors.primary()};
  margin-top: 10px;

  @media (min-width: ${({ theme }) => theme.breakpoints.minWidth.S}) {
    margin-top: 20px;
  }
`;

const HeroContainer = styled.div`
  border-top-left-radius: ${({ theme }) => theme.borders.cardRadius};
  border-top-right-radius: ${({ theme }) => theme.borders.cardRadius};
  overflow: hidden;
`;

type VariantCardProps = {
  actionPosition: 'top' | 'bottom' | 'renderProps';
  'data-test': string;
  badge?: React.ReactNode;
  banner?: JSX.Element;
  children?: React.ReactNode | ((props: any) => React.ReactNode);
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  detailsTo?: any;
  emailTo?: string;
  hero?: React.ReactNode;
  justify?: boolean;
  onClick?: React.DOMAttributes<HTMLButtonElement>['onClick'];
  showAction?: boolean;
  showFooter?: boolean;
  small?: boolean;
  renderHeading?: React.FC<{ className: string }>;
  renderProps?: boolean;
  title: React.ReactNode;
  type?: string;
};

export const VariantCard = (props: VariantCardProps) => {
  const { showAction = true, actionPosition = 'bottom' } = props;
  const RenderHeading = props.renderHeading ?? Heading3;

  const Cta = (
    <ActionButton
      className="animated fadeIn"
      justify={props.justify}
      data-test={props['data-test']}
      type={props.type}
      onClick={props.onClick}
    >
      <Trans id="variantCard.buttonMessage" />
    </ActionButton>
  );

  return (
    <Container className={props.containerClassName} style={props.containerStyle}>
      {props.badge && (
        <BadgeAbsoluteContainer>
          <Badge
            className="font-bold"
            data-test={`${props['data-test']}.badge`}
          >
            {props.badge}
          </Badge>
        </BadgeAbsoluteContainer>
      )}
      {props.hero && <HeroContainer>{props.hero}</HeroContainer>}
      <div style={{ overflow: 'hidden', position: 'relative', width: '100%' }}>
        {props.banner && <Banner>{props.banner}</Banner>}
        <InnerContainer small={props.small}>
          <RenderHeading className="text-center">
            <TextShell>{props.title}</TextShell>
          </RenderHeading>
          {props.renderProps &&
            typeof props.children === 'function' &&
            props.children(Cta)}
          {props.children}
          {showAction && actionPosition === 'bottom' && Cta}
          {props.detailsTo && (
            <DetailsLink className="font-bold" {...props.detailsTo}>
              <Trans id="variantCard.details" />
            </DetailsLink>
          )}
        </InnerContainer>
        {props.showFooter && props.emailTo && (
          <FooterContainer>
            <EmailIcon />
            <RouterLink to={props.emailTo}>
              <Trans id="variantCard.receiveByEmail" />
            </RouterLink>
          </FooterContainer>
        )}
      </div>
    </Container>
  );
};
